/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  SingleValueParameter,
  MultiValueParameter,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { OLAPResult } from "@sa/rep-olap";
import { SelectionMode, AggregationType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { arrayByLength, clone, equal, uniq } from "../../../../shared";
import { SLIDE_NUMBER } from "./constants";
import { DictionariesService, DictionaryItem } from "@sa/rep-mdm";
import { getCalendarMark } from './utils/getDefaultMark';
import { getCurrentYear } from "./utils/dateHelpers";
export interface Parameters {
  commodities: number;
  unit: number;
  frequency: number;
  startDate: number;
  endDate: number;
  calendar: number[];
};

export class SlideContext implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;
  public loading$ = new BehaviorSubject<boolean>(true);
  public olapResult$ = new BehaviorSubject<OLAPResult | null>(null);
  public sources$ = new BehaviorSubject<string | null>(null);

  protected _parameters: BehaviorSubject<Parameters | undefined> = new BehaviorSubject<Parameters | undefined>(undefined);
  public get parameters(): Observable<Parameters | undefined> {
    return this._parameters.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    this._initDefaultMark();

    this._parameters
      .pipe(debounceTime(400))
      .subscribe(async (parameters) => {
        if (parameters) {
          this.readyState = ComponentContextReadyState.Loading;
          this.loading$.next(true);
          this.olapResult$.next(null);


          const { serviceProvider } = this.options;
          const dictionariesService = serviceProvider.resolve<DictionariesService>(ServicesRegister.Dictionary);
          const { calendar } = await getCalendarMark(
            dictionariesService,
            parameters.commodities,
            parameters.frequency
          );
          parameters.calendar = calendar;
          const olapResult = await this.query(parameters);

          this.olapResult$.next(olapResult);
          this.readyState = ComponentContextReadyState.Loaded;
          this.loading$.next(false);
        }
      });
  }

  private async _initDefaultMark() {
    const { dataflowService } = this.options;
    const calendarParam = dataflowService.getParameter("@calendar") as MultiValueParameter;
    const commoditiesParam = dataflowService.getParameter("@commodities") as SingleValueParameter;
    const unitParam = dataflowService.getParameter("@units") as SingleValueParameter;
    const frequencyParam = dataflowService.getParameter("@frequencies") as SingleValueParameter;
    const startDateParam = dataflowService.getParameter("@start_date") as SingleValueParameter;
    const endDateParam = dataflowService.getParameter("@end_date") as SingleValueParameter;

    const { serviceProvider } = this.options;
    const dictionariesService = serviceProvider.resolve<DictionariesService>(ServicesRegister.Dictionary);

    const { calendar } = await getCalendarMark(dictionariesService, commoditiesParam.value as number, frequencyParam.value as number);
    calendarParam.value = calendar;

    this._parameters.next({
      commodities: commoditiesParam.value as number,
      unit: unitParam.value as number,
      frequency: frequencyParam.value as number,
      startDate: startDateParam.value as number,
      endDate: endDateParam.value as number,
      calendar: calendarParam.value as number[],
    });
  }

  async update(command: UpdateCommand): Promise<void> {
    const { serviceProvider } = this.options;
    const commodities = command.values.get("@commodities") as number;
    const unit = command.values.get("@units") as number;
    const frequency = command.values.get("@frequencies") as number;
    const startDate = command.values.get("@start_date") as number;
    const endDate = command.values.get("@end_date") as number;

    if (this._parameters.value !== undefined) {
      const dictionariesService = serviceProvider.resolve<DictionariesService>(ServicesRegister.Dictionary);
      const parameters = clone(this._parameters.value);
      if (commodities !== undefined) {
        parameters.commodities = commodities;
      }
      if (unit !== undefined) {
        parameters.unit = unit;
      }
      if (frequency !== undefined) {
        parameters.frequency = frequency;
      }
      if (startDate !== undefined) {
        parameters.startDate = startDate;
      }
      if (endDate !== undefined) {
        parameters.endDate = endDate;
      }
      // parameters.calendar = calendar;

      if (!equal(parameters, this._parameters.value)) {
        this._parameters.next(parameters);
      }
    }
  }

  async query(parameters: Parameters) {
    const { commodities, calendar, unit, frequency, startDate, endDate } = parameters;
    const { dataSourcesService } = this.options;

    const dataSourcePrms = dataSourcesService.getDataSources(SLIDE_NUMBER, 1, commodities);

    const cubeSlideDef = await dataService.getCubeDefinition(`slide_33`);
    const cubeParameters = clone(cubeSlideDef.parameters);

    cubeParameters["p_commodity_id"]!["defaultValue"] = commodities;
    cubeParameters["p_unit_id"]!["defaultValue"] = unit;
    cubeParameters["p_frequency_id"]!["defaultValue"] = frequency;
    cubeParameters["p_start_date"]!["defaultValue"] = startDate;
    cubeParameters["p_end_date"]!["defaultValue"] = endDate;


    const cubeSlide = await dataService.getCube({
      id: cubeSlideDef.id,
      parameters: cubeParameters,
    });

    const olapQuery = ({
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "calendar.default",
            selection: calendar,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          { path: "commodities.default", selection: [commodities], selectionMode: SelectionMode.Include },
          { path: "units.default", selection: [unit], selectionMode: SelectionMode.Include },
          {
            path: "Measures",
            measures: [
              { id: "value", aggregationType: AggregationType.None },
              { id: "last_year_value", aggregationType: AggregationType.None },
              { id: "two_years_ago_value", aggregationType: AggregationType.None },
            ],
          },
        ].filter(Boolean),
      },
      sourceResolveOptions: {
        sourceId: cubeSlide.id,
      },
    } as any);

    const olapResult = await cubeSlide.query(olapQuery);

    ["units"].filter(Boolean).map(dimension => {
      delete olapResult.data.aggregatedSelection[`${dimension}.default`];
      olapResult.data.rows.forEach(row => delete row.selection[`${dimension}.default`]);
    });

    const dataSource = await dataSourcePrms;
    this.sources$.next(dataSource);
    return olapResult;
  }
}
